import React, { createContext, useState, useEffect, useContext } from 'react';

/**
 * Theme Context
 */
export const ThemeContext = createContext(null);

/**
 * ThemeProvider component to wrap the app and provide theme state
 * @param {Object} props - React props
 * @param {React.ReactNode} props.children - Child components
 */
export const ThemeProvider = ({ children }) => {
    // Initialize theme from localStorage or default to 'dark'
    const [theme, setTheme] = useState(() => {
        const savedTheme = localStorage.getItem('theme');
        // Default to dark mode if not set
        return savedTheme || 'dark';
    });

    // Apply theme class to document element on theme change
    useEffect(() => {
        const root = window.document.documentElement;

        // Remove both classes to ensure clean slate
        root.classList.remove('light', 'dark');

        // Add current theme class
        root.classList.add(theme);

        // Saving to localStorage
        localStorage.setItem('theme', theme);
    }, [theme]);

    /**
     * Toggle between light and dark themes
     */
    const toggleTheme = () => {
        setTheme(prevTheme => prevTheme === 'dark' ? 'light' : 'dark');
    };

    const value = {
        theme,
        toggleTheme,
        isDark: theme === 'dark',
    };

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

/**
 * Custom hook to use theme context
 * @returns {Object} Theme context value
 */
export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export default ThemeProvider;
